import {
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { z } from 'zod'

/**
 * The Firestore boundary.
 *
 * Schemas in `@budgetmonkey/shared` use ISO 8601 UTC strings for timestamps so
 * they stay usable in Cloud Functions and tests without a Firebase dependency
 * (§5). Firestore stores `Timestamp` objects. Conversion happens here, in one
 * place, rather than being remembered at every call site.
 */

/** Firestore `Timestamp` (or anything Date-like) to an ISO 8601 UTC string. */
export function toIso(value: unknown): string | null {
  if (value == null) return null
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'string') return value
  return null
}

/** ISO 8601 string to a Firestore `Timestamp`. */
export function toTimestamp(iso: string | null | undefined): Timestamp | null {
  if (iso == null || iso === '') return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date)
}

/**
 * Build a converter that validates on read and writes Timestamps.
 *
 * Reads are validated rather than cast. A document that doesn't match its
 * schema is a real problem — a half-written record, or a field renamed without
 * a migration — and finding out at the read is far cheaper than a NaN
 * appearing in a budget total three screens later.
 */
export function converterFor<S extends z.ZodType>(
  schema: S,
  timestampFields: readonly string[],
): FirestoreDataConverter<z.output<S>> {
  return {
    toFirestore(model): DocumentData {
      const out: DocumentData = { ...(model as Record<string, unknown>) }
      // `id` is the document key, never a field inside the document.
      delete out.id
      for (const field of timestampFields) {
        if (field in out) {
          out[field] = toTimestamp(out[field] as string | null)
        }
      }
      return out
    },

    fromFirestore(snapshot: QueryDocumentSnapshot): z.output<S> {
      const raw: Record<string, unknown> = { ...snapshot.data(), id: snapshot.id }
      for (const field of timestampFields) {
        if (field in raw) raw[field] = toIso(raw[field])
      }
      const parsed = schema.safeParse(raw)
      if (!parsed.success) {
        throw new Error(
          `Malformed document at ${snapshot.ref.path}: ${parsed.error.issues
            .map((i) => `${i.path.join('.')} ${i.message}`)
            .join('; ')}`,
        )
      }
      return parsed.data as z.output<S>
    },
  }
}

/** `serverTimestamp()` is not usable here — we need the value client-side. */
export const nowIso = (): string => new Date().toISOString()
